import React, { useState, useEffect } from "react";
import { FaArrowCircleLeft, FaArrowCircleRight, FaMinusCircle, FaPlusCircle, FaRegHeart } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { IoHeartCircleOutline } from "react-icons/io5";
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, minusQty, plusQty } from '../../Slices/cart/Cart';
import { addToWish, removeFromWish } from '../../Slices/cart/Wish';

const ProductSlider = ({ data, name }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [itemsPerView, setItemsPerView] = useState(4); // Default for desktop
  const cart = useSelector(state => state.cart);
  const wish = useSelector(state => state.wish);
  const dispatch = useDispatch();
  const weburl = useSelector(state => state.constants.weburl);

  // Calculate items per view based on screen size
  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 640) { // Mobile
        setItemsPerView(2);
      } else if (window.innerWidth < 768) { // Tablet
        setItemsPerView(3);
      } else if (window.innerWidth < 1024) { // Small desktop
        setItemsPerView(4);
      } else { // Large desktop
        setItemsPerView(6);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  // Add extra items for circular effect
  useEffect(() => {
    if (data.length > itemsPerView) {
      const extendedItems = [
        ...data,
        ...data.slice(0, itemsPerView),
      ];
      setItems(extendedItems);
    } else {
      setItems(data);
    }
  }, [data, itemsPerView]);

  // Auto-slide functionality (commented out due to long interval)
  useEffect(() => {
    if (data.length > itemsPerView) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
      }, 5000); // Change every 5 seconds

      return () => clearInterval(interval);
    }
  }, [data.length, itemsPerView]);

  const goNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
  };

  const goPrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + data.length) % data.length);
  };

  const showRightArrow = data.length > itemsPerView;
  const showLeftArrow = data.length > itemsPerView && currentIndex > 0;

  // Calculate slide width percentage
  const slideWidth = 100 / itemsPerView;

  return (
    <div className="w-full px-2 sm:px-4 mt-4 border-b border-gray-200" data-aos="fade-up">
      <div className="p-2 mb-4">
        <h1 className="xl:text-2xl lg:text-2xl md:text-xl text-lg font-serif text-left p-2">
          <span className="p-2 rounded-sm font-bold">{name}</span>
        </h1>
      </div>
      
      <div className="relative overflow-hidden px-2 sm:px-0">
        {/* Slider Container */}
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * slideWidth}%)`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="flex-shrink-0 p-1 sm:p-2"
              style={{ width: `${slideWidth}%` }}
            >
              <div className='cursor-pointer shadow-md hover:shadow-lg transition-shadow rounded-lg bg-white h-full flex flex-col'>
                {/* Image Container */}
                <div className='relative transition-all duration-700 ease-in-out hover:scale-105 p-2'>
                  <Link to={`/singleproduct/${item.id}`}>
                    <div className='aspect-square overflow-hidden rounded-lg bg-gray-50'>
                      <img 
                        alt='newitem' 
                        className='w-full h-full object-contain p-2' 
                        src={item.image ? `${weburl}/${item.image}` : `images/placeholder.png`} 
                      />
                    </div>
                  </Link>

                  {/* Wishlist Icon */}
                  <div className='absolute top-1 right-1 sm:top-2 sm:right-2 z-10'>
                    {wish.some(wishItem => wishItem.id === item.id) ? (
                      <IoHeartCircleOutline 
                        size={28} 
                        className='text-red-800 cursor-pointer hover:scale-110 transition' 
                        onClick={() => dispatch(removeFromWish(item))} 
                      />
                    ) : (
                      <FaRegHeart 
                        size={22} 
                        className='text-gray-600 cursor-pointer hover:text-red-600 transition' 
                        onClick={() => dispatch(addToWish(item))} 
                      />
                    )}
                  </div>

                  {/* Free Shipping Badge */}
                  {item.shipping === '1' && (
                    <div className='absolute bottom-1 left-1 z-10 animate-pulse'>
                      <div className='bg-yellow-400 text-black px-1.5 py-0.5 rounded-md text-[8px] sm:text-[10px] font-bold'>
                        🚚 Free Ship
                      </div>
                    </div>
                  )}

                  {/* Discount Badge */}
                  {item.unitretailprice > item.unitsaleprice && (
                    <div className='absolute bottom-1 right-1 z-10'>
                      <div className='bg-red-600 text-white px-1.5 py-0.5 rounded-md text-[8px] sm:text-[10px] font-bold'>
                        {Math.round(((item.unitretailprice - item.unitsaleprice) / item.unitretailprice) * 100)}% OFF
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className='p-2 sm:p-3 flex-grow flex flex-col'>
                  <div className='flex justify-between items-start'>
                    <p className='text-gray-400 text-[10px] sm:text-xs truncate'>{item.category_name}</p>
                  </div>

                  {item.unitretailprice === '0' ? (
                    <div className="mt-2">
                      <p className='text-xs sm:text-sm font-medium line-clamp-2'>{item.name.slice(0, 40)}...</p>
                    </div>
                  ) : (
                    <>
                      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 mt-1'>
                        <Link to={`/singleproduct/${item.id}`}>
                          <h2 className='text-sm sm:text-base font-bold text-green-600'>
                            Rs:{item.unitsaleprice}
                          </h2>
                        </Link>
                        <del className='text-[10px] sm:text-xs text-gray-500'>
                          Rs:{item.unitretailprice}
                        </del>
                      </div>
                      
                      <p className='text-[11px] sm:text-sm text-gray-700 mt-1 line-clamp-2'>
                        {item.name.slice(0, 40)}...
                      </p>
                    </>
                  )}

                  {/* Add to Cart Button */}
                  <div className="mt-2 sm:mt-3">
                    {item.unitretailprice !== '0' ? (
                      cart.some(cartitem => cartitem.id === item.id) ? (
                        <div className='flex transition-all duration-700 ease-in-out justify-around items-center w-full bg-gray-50 border h-8 sm:h-10 rounded-full'>
                          <FaMinusCircle 
                            className='font-extrabold text-green-600 text-[20px] sm:text-[25px] hover:text-green-500 cursor-pointer' 
                            onClick={() => dispatch(minusQty(item.id))}
                          />
                          <span className='font-bold text-[18px] sm:text-[22px]'>
                            {cart.find(cartitem => cartitem.id === item.id).qty}
                          </span>
                          <FaPlusCircle 
                            className='font-extrabold text-red-600 text-[20px] sm:text-[25px] hover:text-red-500 cursor-pointer' 
                            onClick={() => dispatch(plusQty(item.id))}
                          />
                        </div>
                      ) : (
                        <Link 
                          onClick={() => dispatch(addToCart(item))} 
                          className='transition-all flex justify-center p-1 items-center duration-700 ease-in-out hover:scale-105 w-full sm:w-24 border border-gray-950 rounded-full'
                        >
                          <p className='font-bold text-gray-700 text-sm sm:text-base'>+ Add</p>
                        </Link>
                      )
                    ) : (
                      <Link to={'/quote'} className='transition-all'>
                        <div className='transition-all flex justify-center p-1 items-center duration-700 ease-in-out hover:scale-105 w-full sm:w-24 border border-gray-950 rounded-full'>
                          <p className='font-bold text-gray-700 text-sm sm:text-base'>Options</p>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        {showLeftArrow && (
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-lg z-10 transition-all duration-300 hover:scale-110"
            aria-label="Previous products"
          >
            <FaArrowCircleLeft className="text-gray-600 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
          </button>
        )}

        {showRightArrow && (
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-lg z-10 transition-all duration-300 hover:scale-110"
            aria-label="Next products"
          >
            <FaArrowCircleRight className="text-gray-600 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
          </button>
        )}

        {/* Dots Indicator (Optional) */}
        {data.length > itemsPerView && (
          <div className="flex justify-center gap-1 sm:gap-2 mt-4 pb-4">
            {Array.from({ length: Math.ceil(data.length / itemsPerView) }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx * itemsPerView)}
                className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentIndex / itemsPerView) === idx
                    ? 'w-4 sm:w-6 bg-blue-600'
                    : 'w-1.5 sm:w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSlider;